/**
 * The non-colour identifier that travels with every Tide colour: a small SVG
 * shape filled with the category colour. Decorative by itself (aria-hidden);
 * the text label next to it carries the meaning.
 */
import React, { useId } from 'react';
import type { MarkerShape } from './tokens';

export function Marker({ shape, color, size = 12, className, glyph }: {
  shape: MarkerShape; color: string; size?: number; className?: string;
  /** The code drawn by the `glyph` shape (status words). */
  glyph?: string;
}) {
  const raw = useId();
  if (shape === 'glyph') {
    // Colour on the border (a 3:1 mark), letters in ink (a 4.5:1 text): the code stays readable in every word colour.
    return (
      <span aria-hidden="true" className={`tide-glyph ${className || ''}`.trim()} data-marker="glyph" data-glyph={glyph} style={{ borderColor: color }}>
        {glyph || '?'}
      </span>
    );
  }
  const patternId = `tide-hatch-${raw.replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const s = size;
  let body: React.ReactNode;
  switch (shape) {
    case 'circle':
      body = <circle cx={s / 2} cy={s / 2} r={s / 2 - 0.5} fill={color} />;
      break;
    case 'triangle':
      body = <polygon points={`${s / 2},0.5 ${s - 0.5},${s - 0.5} 0.5,${s - 0.5}`} fill={color} />;
      break;
    case 'diamond':
      body = <polygon points={`${s / 2},0.5 ${s - 0.5},${s / 2} ${s / 2},${s - 0.5} 0.5,${s / 2}`} fill={color} />;
      break;
    case 'dash':
      body = <rect x={0} y={s / 2 - 1.5} width={s} height={3} rx={1} fill={color} />;
      break;
    case 'hatch':
      body = (
        <>
          <defs>
            <pattern id={patternId} width={4} height={4} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
              <rect width={4} height={4} fill="var(--tide-surface, #fff)" />
              <rect width={2} height={4} fill={color} />
            </pattern>
          </defs>
          <rect x={0.5} y={0.5} width={s - 1} height={s - 1} rx={1.5} fill={`url(#${patternId})`} stroke={color} strokeWidth={1} />
        </>
      );
      break;
    case 'square':
    default:
      body = <rect x={0.5} y={0.5} width={s - 1} height={s - 1} rx={2} fill={color} />;
  }
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      width={s}
      height={s}
      viewBox={`0 0 ${s} ${s}`}
      className={className}
      data-marker={shape}
      style={{ flex: 'none', display: 'inline-block', verticalAlign: 'middle' }}
    >
      {body}
    </svg>
  );
}

/**
 * An SVG <pattern> for hatched chart fills (unknown-age bars, import series).
 * Put it inside a recharts chart's <defs> and fill with `url(#id)`.
 */
export function HatchPattern({ id, color }: { id: string; color: string }) {
  return (
    <pattern id={id} width={6} height={6} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
      <rect width={6} height={6} fill="var(--tide-surface, #fff)" />
      <rect width={3} height={6} fill={color} />
    </pattern>
  );
}
